import { appEquipmentConfigState, equipmentTypesByIdState, roomsState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import {
    type DataKey,
    type Equipment,
    EQUIPMENT_CORE_FIELD_KEYS,
    type EquipmentFields,
    type GuideValueType,
    type InputField,
} from '@rock/types';
import { DimensionValueUtil, equipmentIdentity, unitBrandName } from '@rock/util';
import { isArray, keyBy, sortBy } from 'lodash';

export type EquipmentFieldValues = {
    dataKey: DataKey;
    label: string;
    value: GuideValueType;
    displayValue: string;
}[];

export function useEquipmentFieldValues(equipment: Equipment) {
    const typesById = useAsyncAtomValue(equipmentTypesByIdState(equipment.spaceId), {});
    const type = typesById[equipment.typeId];

    const appEquipmentConfig = useAsyncAtomValue(appEquipmentConfigState);
    // Unlike the app's twin, this keeps DISABLED core fields: an admin looking at a home's equipment
    // should see every value it stored, including one captured while a field was still switched on.
    const coreFields = appEquipmentConfig?.coreFields;
    const inputFields = type?.inputFields;
    const allFields = [...(coreFields || []), ...(inputFields || [])];

    const rooms = useAsyncAtomValue(roomsState, []);
    const roomsById = keyBy(rooms, 'id');

    const equipmentFieldValues: EquipmentFields = equipment.fields || {};
    const fieldValues: EquipmentFieldValues = [];
    const identity = equipmentIdentity(equipment);

    for (const field of allFields) {
        if (field.dataType === 'image') {
            continue;
        }

        let fieldValue = equipmentFieldValues[field.dataKey];

        if (field.dataKey === EQUIPMENT_CORE_FIELD_KEYS.BRAND) {
            fieldValue = unitBrandName(equipment);
        }
        if (field.dataKey === EQUIPMENT_CORE_FIELD_KEYS.ROOM_ID && equipment.fields?.roomId) {
            fieldValue = roomsById[equipment.fields.roomId]?.name ?? '';
        }
        // The identity field — a model number, or the product name where there is none — shows what the
        // enrichment resolved the observation to, the same rule every other surface reads.
        if (identity && field.dataKey === identity.dataKey) {
            fieldValue = identity.display;
        }

        if (fieldValue) {
            fieldValues.push({
                dataKey: field.dataKey,
                label: field.label,
                value: fieldValue,
                displayValue: isArray(fieldValue)
                    ? fieldValue.join(', ')
                    : typeof fieldValue === 'boolean'
                      ? `${fieldValue ? 'Yes' : 'No'}`
                      : typeof fieldValue === 'number'
                        ? `${fieldValue}`
                        : typeof fieldValue === 'string'
                          ? renderStringValue(field, fieldValue)
                          : '',
            });
        }
    }

    return sortBy(fieldValues, 'label');
}

function renderStringValue(field: InputField, fieldValue: string): string {
    if (field.dataType === 'date') {
        const d = new Date(fieldValue);
        if (!Number.isNaN(d.getTime())) {
            return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
        }
        return fieldValue;
    }

    if (field.dataType === 'month-year') {
        const [y, m] = fieldValue.split('-');
        if (y && m) {
            const d = new Date(Number(y), Number(m) - 1);
            return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(d);
        }
        return fieldValue;
    }

    if (field.dataType === 'dimensions' && field.dimensionsSubType) {
        return DimensionValueUtil.formatDisplay(
            fieldValue,
            field.dimensionsSubType,
            field.defaultLengthSelection || 'ft',
            !!field.renderCalculatedValue,
        );
    }

    if (field.dataType === 'select') {
        return field.options?.find((o) => o.value === fieldValue)?.title ?? fieldValue;
    }

    return `${fieldValue}`;
}
